import { getQueryParamsString, PartnerSkills } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import { createSelectOptions } from "Infrastructure/Utilities/Helpers/createSelectOptions";
import {
	getPartnerFilterFormSchema,
	type PartnerFilterFormModel,
} from "Partner/Components/Form/Schema/partnerFilterFormSchema";
import { PartnerPaths } from "Partner/Routes/Routing";
import Input from "Shared/Components/Form/CustomFormComponent/Input";
import Select from "Shared/Components/Form/CustomFormComponent/Select";
import {
	DEFAULT_PAGE_INDEX,
	DEFAULT_PAGE_LIMIT,
} from "Shared/Hooks/useTableFilter";
import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Button, Grid2, Stack } from "@mui/material";
import type { FC } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router";

type Props = {
	setIsFilterDrawerOpen: (isOpen: boolean) => void;
};

const { shared } = Resources.base.partner;

export const PartnerFilterForm: FC<Props> = ({ setIsFilterDrawerOpen }) => {
	const { t } = useResource();
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();

	const form = useForm<PartnerFilterFormModel>({
		resolver: zodResolver(getPartnerFilterFormSchema()),
		defaultValues: {
			query: searchParams.get("query") || "",
			skills: (searchParams.getAll("skills") as PartnerSkills[]) ?? [],
		},
	});

	const {
		control,
		handleSubmit,
		formState: { errors },
	} = form;

	const handleSubmitFilter = () => {
		handleSubmit((data) => {
			const filteredData = Object.fromEntries(
				Object.entries(data).filter(
					([_, value]) => value !== null && value !== undefined && value !== "",
				),
			);
			navigate(
				`${PartnerPaths.Index.path}${getQueryParamsString({ ...filteredData, pageLimit: DEFAULT_PAGE_LIMIT, pageIndex: DEFAULT_PAGE_INDEX })}`,
			);
			setIsFilterDrawerOpen(false);
		})();
	};

	const handleResetFilter = () => {
		form.reset({
			query: "",
			skills: [],
		});
		navigate(
			`${PartnerPaths.Index.path}${getQueryParamsString({ pageLimit: DEFAULT_PAGE_LIMIT, pageIndex: DEFAULT_PAGE_INDEX })}`,
		);
	};

	return (
		<>
			<Grid2 container spacing={1}>
				<Grid2 size={{ xs: 12 }}>
					<Input
						id="query"
						name="query"
						label={t(shared.query)}
						variant="outlined"
						control={control}
						errors={errors}
						fullWidth
						sxLabel={{ mt: 0 }}
					/>
				</Grid2>
				<Grid2 size={{ xs: 12 }}>
					<Select
						id="skills"
						label={t(Resources.base.partner.detail.generalInfo.skills)}
						fullWidth
						options={createSelectOptions(
							PartnerSkills,
							t,
							Resources.base.partner.selectOptions.skills,
						)}
						variant="outlined"
						name="skills"
						control={control}
						errors={errors}
						multiple
					/>
				</Grid2>
			</Grid2>
			<Stack mt={2} direction="row" spacing={2}>
				<Box>
					<Button variant="contained" onClick={handleSubmitFilter}>
						{t(shared.search)}
					</Button>
				</Box>
				<Box>
					<Button variant="outlined" onClick={handleResetFilter}>
						{t(shared.reset)}
					</Button>
				</Box>
			</Stack>
		</>
	);
};
